import { useMemo } from 'react';
import { ScopeEnum } from 'src/domain/enums/split-level';
import { useChainFurnitureZoneQuery } from 'src/hooks/chain-furniture-zone/queries/useChainFurnitureZoneQuery';
import { StoreModel } from 'src/infrastructure/rest-api/api-types';
import { HeatMapTypeEnum } from 'src/mapping/projected-option-count.mapping';
import { useInventoryMasterDataRows } from '../inventory-master-data/table/useInventoryMasterDataRows';
import { useCurrentStore, useFilteredStores } from '../store';
import { useChainStoresInventoryQuery } from '../store-inventory-management/queries/useStoreInventoryData';
import { useStorefrontType } from '../style-timeline/useStorefrontType';
import { useScope } from '../useScope';

export interface ProjectedOptionCountInventory {
    id: number;
    name: string;
    options: number;
    enabled: boolean;
}

export const useProjectedOptionCountInventory = (type: HeatMapTypeEnum) => {
    const scope = useScope();
    const [stores] = useFilteredStores();
    const currentStore = useCurrentStore();
    const storefrontType = useStorefrontType();
    const { data: chainFurniture } = useInventoryMasterDataRows();
    const { data: zones } = useChainFurnitureZoneQuery();
    const { data: allStoreInventoryData } = useChainStoresInventoryQuery(
        currentStore?.storefrontType ?? storefrontType
    );

    const zoneNameById = useMemo(() => {
        return new Map((zones ?? []).map((zone) => [zone.id, zone.name]));
    }, [zones]);

    const inventory: ProjectedOptionCountInventory[] = useMemo(() => {
        const storesLookup: Map<number, StoreModel> = new Map();
        if (scope === ScopeEnum.STORE && currentStore) {
            storesLookup.set(currentStore.id, currentStore);
        } else {
            for (const store of stores) {
                if (store.storefrontType === storefrontType) {
                    storesLookup.set(store.id, store);
                }
            }
        }
        const inventory = allStoreInventoryData?.filter((inv) => storesLookup.has(inv.storeId));

        if (type === HeatMapTypeEnum.Zone) {
            const zoneMap = new Map<number, ProjectedOptionCountInventory>();

            for (const inv of inventory ?? []) {
                const zoneId = inv.chainFurnitureZoneId;
                if (zoneId == null) {
                    continue;
                }

                const zoneName = zoneNameById.get(zoneId) ?? 'Unknown';
                const currentZone = zoneMap.get(zoneId) ?? {
                    id: zoneId,
                    name: zoneName,
                    options: 0,
                    enabled: false,
                };

                currentZone.options += inv.numberOfOptions ?? 0;
                currentZone.enabled = currentZone.options > 0;
                zoneMap.set(zoneId, currentZone);
            }

            return Array.from(zoneMap.values());
        }

        return (inventory ?? []).map((inv) => {
            const furniture = chainFurniture?.find((cf) => cf.id === inv.chainFurnitureId);
            return {
                id: inv.chainFurnitureId,
                name: furniture?.name || 'N/A',
                options: inv.numberOfOptions,
                enabled: inv.numberOfOptions > 0,
            };
        });
    }, [allStoreInventoryData, currentStore, scope, stores, storefrontType, chainFurniture, zoneNameById, type]);

    return inventory;
};
